import { useLocation, type Location } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Settings-as-overlay plumbing (the "background location" modal-route pattern)
 * — MOBILE ONLY. On desktop /settings is an ordinary page (left rail + content
 * column) and none of this applies.
 *
 * On mobile, settings is a detour, not a destination: every in-app entry point
 * navigates to `/settings` carrying the CURRENT location as
 * `state.backgroundLocation`. The route tree keeps rendering that background
 * location, so the page the user was on stays mounted — scroll, filters,
 * everything — beneath the overlay, and closing it drops them back exactly
 * where they were.
 *
 * Direct loads (refresh, shared link, OS deep link) have no background; the
 * overlay then renders over a blank shell and closing falls back to
 * `/login-success`, which forwards every role to its correct home.
 */

/** The new-flat alerts block in Settings → notifications (`FlatAlertsBlock`): its element id. */
export const KOTWICA_ALERTOW = 'flat-alerts';
/** Where the SERP filter panel's alert bell goes: the notifications section, scrolled to the alerts block. */
export const ADRES_USTAWIEN_ALERTOW = `/settings?section=notifications#${KOTWICA_ALERTOW}`;

interface ModalLocationState {
  backgroundLocation?: Location;
  /** How many history entries the overlay owns (hub + pushed drill-down
   *  sections). The X pops them all; the phone's back gesture pops one. */
  modalDepth?: number;
}

/** Link `state` for an in-app settings entry point. Returns `undefined` when
 *  already on /settings — re-opening from inside must not stack a settings
 *  location as its own background (double overlay). */
export function settingsLinkState(location: Location): ModalLocationState | undefined {
  if (location.pathname === '/settings') return undefined;
  return { backgroundLocation: location };
}

/** The `state` prop for a Link to /settings. Mobile: the background location
 *  (opens the overlay above the current page). Desktop: `undefined` — settings
 *  is a plain page there, and carrying the state would both keep the overlay
 *  plumbing engaged and stop ScrollToTop from resetting the scroll. */
export function useSettingsLinkState(): ModalLocationState | undefined {
  const location = useLocation();
  const isMobile = useIsMobile();
  return isMobile ? settingsLinkState(location) : undefined;
}

/** The background location a modal navigation carried, if any. Ignores a
 *  /settings background (defensive — see settingsLinkState). */
export function getBackgroundLocation(location: Location): Location | undefined {
  const state = location.state as ModalLocationState | null | undefined;
  const background = state?.backgroundLocation;
  if (!background || background.pathname === '/settings') return undefined;
  return background;
}
